import { Tables } from '../../supabase/database'
import { Accessor, Setter, createEffect, createSignal, on, onCleanup } from 'solid-js'
import { backendAxios } from '../../../axios'
import type { Nullable } from '../../../typescript'
import toastError from '../../AlertBag/toastError'
import { isOfflineOnly } from '../../core/isOfflineOnly'
import { loadAvatar } from '../../idb/avatars'

type FileId = Tables<'files'>['id']
type FileBlobString = string

// no idea why, but not manually specifying this type caused linting errors
type PathToObjectUrlSignal = [Accessor<Nullable<FileBlobString>>, Setter<Nullable<FileId>>]

/**
 * Creates a signal that loads a file/blob from an ID.
 * 
 * Caching strategy:
 * - IndexedDB is checked first (fast path for cached avatars)
 * - If not in IndexedDB and online: fetch from backend, cache to IndexedDB
 * - If not in IndexedDB and offline: silently skip (no error toast)
 * 
 * Avatars deviate from the normal user-chooses-disk philosophy:
 * - Online builds: backend is primary storage, IndexedDB is a cache
 * - Offline builds: IndexedDB is the primary storage for user customization
 */
export default function createFileIdToObjectUrlSignal(
  initialValue: FileId | null = null
): PathToObjectUrlSignal {
  const [fileId, setFileId] = createSignal<Nullable<FileId>>(initialValue)
  const [fileBlob, setFileBlob] = createSignal<Nullable<FileBlobString>>(null)

  createEffect(() => {
    const id = fileId()
    if (id) {
      downloadImage(id)
    } else {
      setFileBlob(null)
    }
  })

  const downloadImage = async (fileId: FileId) => {
    // Try IndexedDB first (works for both online cache and offline primary storage)
    try {
      const cachedBlob = await loadAvatar(fileId)
      if (cachedBlob) {
        const url = URL.createObjectURL(cachedBlob)
        setFileBlob((prev) => {
          if (prev) {
            URL.revokeObjectURL(prev)
          }
          return url
        })
        return
      }
    } catch (error) {
      console.error('Failed to load avatar from IndexedDB:', error)
      // Continue to backend if IndexedDB fails
    }

    // Not in IndexedDB - check if we're offline
    if (isOfflineOnly) {
      // Silently skip - avatar not available in offline mode
      // No error toast since this is expected behavior
      return
    }

    // Online mode: fetch from backend and cache to IndexedDB
    try {
      const response = await backendAxios.get(`/file/${fileId}/data`, {
        responseType: 'blob',
      })
      const blob: Blob = response.data

      // Cache to IndexedDB for future offline viewing
      const { saveAvatar } = await import('../../idb/avatars')
      await saveAvatar(fileId, blob)

      const url = URL.createObjectURL(blob)
      setFileBlob((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev)
        }
        return url
      })
    } catch (error) {
      console.error(error)
      toastError(error)
    }
  }

  onCleanup(() => {
    const blobUrl = fileBlob()
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl)
    }
  })
  createEffect(on(fileBlob, (
    newBloblUrl: Nullable<FileBlobString>,
    oldBlobUrl: Nullable<FileBlobString>) => {
    if (oldBlobUrl) {
      URL.revokeObjectURL(oldBlobUrl)
    }
  }))

  return [fileBlob, setFileId]
}
